/**
 * Ladenplan als SVG auf einem Raster. Anzeige (Belegung, Mieterportal, Kasse) und Bearbeitung (Drag & Drop,
 * Größe ziehen) in einer Komponente; `bearbeitbar` schaltet die Zeiger-Interaktion ein. Farben nur über Tokens.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { PlanElement, PlanPlatz } from "@shared/plan";
import { ELEMENT_TEXT } from "@shared/plan";

export type PlanBelegung = "frei" | "belegt" | "bald_frei" | "reserviert" | "inaktiv";
export interface PlanRegal {
  id: string;
  nummer: string;
  platz: PlanPlatz | null;
  belegung?: PlanBelegung;
  mieterName?: string | null;
}

const FARBE: Record<PlanBelegung, { fill: string; stroke: string; text: string }> = {
  frei: { fill: "var(--surface-raised)", stroke: "var(--line-strong)", text: "var(--ink)" },
  belegt: { fill: "var(--success-soft)", stroke: "var(--success)", text: "var(--ink)" },
  bald_frei: { fill: "var(--attention-soft)", stroke: "var(--attention)", text: "var(--ink)" },
  reserviert: { fill: "var(--violett-soft)", stroke: "var(--violett)", text: "var(--ink)" },
  inaktiv: { fill: "var(--surface-sunken)", stroke: "var(--line)", text: "var(--ink-muted)" },
};
const ELEMENT_FARBE: Record<PlanElement["art"], { fill: string; stroke: string }> = {
  wand: { fill: "var(--ink-muted)", stroke: "var(--ink-muted)" },
  tuer: { fill: "var(--limette)", stroke: "var(--limette-ink)" },
  kasse: { fill: "var(--violett)", stroke: "var(--violett-strong)" },
  text: { fill: "transparent", stroke: "transparent" },
};

type Ziel = { art: "regal" | "element"; id: string };
type Ziehen = { ziel: Ziel; modus: "verschieben" | "groesse"; startX: number; startY: number; platz: PlanPlatz };

export function Ladenplan({
  spalten,
  zeilen,
  elemente,
  regale,
  hintergrundUrl,
  hervorgehoben = [],
  ausgewaehlt = null,
  bearbeitbar = false,
  onRegalKlick,
  onAuswahl,
  onPlatz,
  maxHoehe,
}: {
  spalten: number;
  zeilen: number;
  elemente: PlanElement[];
  regale: PlanRegal[];
  hintergrundUrl?: string | null;
  /** Regal-IDs, die hervorgehoben werden (z. B. das eigene Regal im Mieterportal). */
  hervorgehoben?: string[];
  ausgewaehlt?: Ziel | null;
  bearbeitbar?: boolean;
  onRegalKlick?: (regal: PlanRegal) => void;
  onAuswahl?: (ziel: Ziel | null) => void;
  /** Bearbeiten: neuer Platz für Regal oder Element (nach Loslassen). */
  onPlatz?: (ziel: Ziel, platz: PlanPlatz) => void;
  maxHoehe?: number;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [ziehen, setZiehen] = useState<Ziehen | null>(null);
  const [vorschau, setVorschau] = useState<PlanPlatz | null>(null);
  const klickIgnorieren = useRef(false);
  const Z = 40; // Einheiten je Feld im viewBox
  const breite = spalten * Z;
  const hoehe = zeilen * Z;

  function feldAus(e: ReactPointerEvent): { fx: number; fy: number } {
    const r = svgRef.current!.getBoundingClientRect();
    return { fx: ((e.clientX - r.left) / r.width) * spalten, fy: ((e.clientY - r.top) / r.height) * zeilen };
  }
  function start(e: ReactPointerEvent<SVGGElement>, ziel: Ziel, platz: PlanPlatz, modus: Ziehen["modus"]) {
    if (!bearbeitbar) return;
    e.stopPropagation();
    e.preventDefault();
    const { fx, fy } = feldAus(e);
    (e.currentTarget.ownerSVGElement ?? e.currentTarget).setPointerCapture?.(e.pointerId);
    setZiehen({ ziel, modus, startX: fx, startY: fy, platz });
    setVorschau(platz);
    onAuswahl?.(ziel);
  }
  function bewegen(e: ReactPointerEvent<SVGSVGElement>) {
    if (!ziehen) return;
    const { fx, fy } = feldAus(e);
    const dx = Math.round(fx - ziehen.startX);
    const dy = Math.round(fy - ziehen.startY);
    const p = ziehen.platz;
    const neu: PlanPlatz =
      ziehen.modus === "verschieben"
        ? { ...p, x: Math.min(Math.max(0, p.x + dx), spalten - p.b), y: Math.min(Math.max(0, p.y + dy), zeilen - p.h) }
        : { ...p, b: Math.min(Math.max(1, p.b + dx), spalten - p.x), h: Math.min(Math.max(1, p.h + dy), zeilen - p.y) };
    setVorschau(neu);
  }
  function ende() {
    if (!ziehen) return;
    if (vorschau && (vorschau.x !== ziehen.platz.x || vorschau.y !== ziehen.platz.y || vorschau.b !== ziehen.platz.b || vorschau.h !== ziehen.platz.h)) onPlatz?.(ziehen.ziel, vorschau);
    // Der Klick nach dem Loslassen soll die Auswahl nicht aufheben
    klickIgnorieren.current = true;
    setTimeout(() => (klickIgnorieren.current = false), 0);
    setZiehen(null);
    setVorschau(null);
  }
  const platzFuer = (ziel: Ziel, p: PlanPlatz) => (ziehen && ziehen.ziel.art === ziel.art && ziehen.ziel.id === ziel.id && vorschau ? vorschau : p);
  const istAusgewaehlt = (ziel: Ziel) => ausgewaehlt?.art === ziel.art && ausgewaehlt.id === ziel.id;

  return (
    <svg
      ref={svgRef}
      className="app-plan"
      viewBox={`0 0 ${breite} ${hoehe}`}
      style={{ width: "100%", height: "auto", maxHeight: maxHoehe, display: "block", touchAction: bearbeitbar ? "none" : "auto", userSelect: "none" }}
      role="img"
      aria-label={`Ladenplan mit ${regale.filter((r) => r.platz).length} Regalen`}
      onPointerMove={bewegen}
      onPointerUp={ende}
      onPointerCancel={ende}
      onClick={() => {
        if (!klickIgnorieren.current) onAuswahl?.(null);
      }}
    >
      <rect x={0} y={0} width={breite} height={hoehe} fill="var(--surface-sunken)" rx={8} />
      {hintergrundUrl ? <image href={hintergrundUrl} x={0} y={0} width={breite} height={hoehe} preserveAspectRatio="none" opacity={0.55} /> : null}
      <g stroke="var(--line)" strokeWidth={1}>
        {Array.from({ length: spalten + 1 }, (_, i) => (
          <line key={`v${i}`} x1={i * Z} y1={0} x2={i * Z} y2={hoehe} />
        ))}
        {Array.from({ length: zeilen + 1 }, (_, i) => (
          <line key={`h${i}`} x1={0} y1={i * Z} x2={breite} y2={i * Z} />
        ))}
      </g>
      {elemente.map((el) => {
        const ziel: Ziel = { art: "element", id: el.id };
        const p = platzFuer(ziel, el);
        const f = ELEMENT_FARBE[el.art];
        return (
          <g key={el.id} onPointerDown={(e) => start(e, ziel, el, "verschieben")} style={{ cursor: bearbeitbar ? "move" : "default" }}>
            <rect x={p.x * Z + 2} y={p.y * Z + 2} width={p.b * Z - 4} height={p.h * Z - 4} rx={el.art === "wand" ? 2 : 8} fill={f.fill} stroke={istAusgewaehlt(ziel) ? "var(--focus)" : f.stroke} strokeWidth={istAusgewaehlt(ziel) ? 4 : 2} strokeDasharray={el.art === "text" ? "6 4" : undefined} />
            {el.art !== "wand" ? (
              <text x={(p.x + p.b / 2) * Z} y={(p.y + p.h / 2) * Z} textAnchor="middle" dominantBaseline="central" fontSize={Math.min(14, p.h * Z * 0.45)} fontWeight={600} fill={el.art === "text" ? "var(--ink-muted)" : el.art === "tuer" ? "var(--on-limette)" : "var(--on-violett)"}>
                {el.text || ELEMENT_TEXT[el.art]}
              </text>
            ) : null}
            {bearbeitbar && istAusgewaehlt(ziel) ? <rect x={(p.x + p.b) * Z - 12} y={(p.y + p.h) * Z - 12} width={12} height={12} fill="var(--focus)" style={{ cursor: "nwse-resize" }} onPointerDown={(e) => start(e, ziel, el, "groesse")} /> : null}
          </g>
        );
      })}
      {regale.map((r) => {
        if (!r.platz) return null;
        const ziel: Ziel = { art: "regal", id: r.id };
        const p = platzFuer(ziel, r.platz);
        const hervor = hervorgehoben.includes(r.id);
        const f = FARBE[r.belegung ?? "frei"];
        const klein = Math.min(p.b, p.h) * Z;
        return (
          <g
            key={r.id}
            onPointerDown={(e) => start(e, ziel, r.platz!, "verschieben")}
            onClick={(e) => {
              e.stopPropagation();
              if (!bearbeitbar) onRegalKlick?.(r);
            }}
            style={{ cursor: bearbeitbar ? "move" : onRegalKlick ? "pointer" : "default" }}
            role={onRegalKlick ? "button" : undefined}
            tabIndex={onRegalKlick ? 0 : undefined}
            aria-label={`Regal ${r.nummer}${r.belegung ? `, ${r.belegung.replace("_", " ")}` : ""}`}
            onKeyDown={(e) => {
              if (!bearbeitbar && onRegalKlick && (e.key === "Enter" || e.key === " ")) onRegalKlick(r);
            }}
          >
            <rect x={p.x * Z + 3} y={p.y * Z + 3} width={p.b * Z - 6} height={p.h * Z - 6} rx={6} fill={hervor ? "var(--limette)" : f.fill} stroke={istAusgewaehlt(ziel) ? "var(--focus)" : hervor ? "var(--limette-ink)" : f.stroke} strokeWidth={istAusgewaehlt(ziel) || hervor ? 4 : 2} />
            <text x={(p.x + p.b / 2) * Z} y={(p.y + p.h / 2) * Z} textAnchor="middle" dominantBaseline="central" fontSize={Math.min(16, klein * 0.42)} fontWeight={700} fill={hervor ? "var(--on-limette)" : f.text} style={{ fontVariantNumeric: "tabular-nums" }}>
              {r.nummer}
            </text>
            {bearbeitbar && istAusgewaehlt(ziel) ? <rect x={(p.x + p.b) * Z - 12} y={(p.y + p.h) * Z - 12} width={12} height={12} fill="var(--focus)" style={{ cursor: "nwse-resize" }} onPointerDown={(e) => start(e, ziel, r.platz!, "groesse")} /> : null}
          </g>
        );
      })}
    </svg>
  );
}

export function PlanLegende({ mitStatus = true }: { mitStatus?: boolean }) {
  const eintraege: [PlanBelegung, string][] = mitStatus ? [["frei", "Frei"], ["belegt", "Belegt"], ["bald_frei", "Bald frei"], ["reserviert", "Reserviert"]] : [];
  return (
    <div className="sc-toolbar sc-small" aria-hidden="true">
      {eintraege.map(([k, t]) => (
        <span key={k} className="sc-legend"><i style={{ background: FARBE[k].fill, border: `2px solid ${FARBE[k].stroke}` }} /> {t}</span>
      ))}
      <span className="sc-legend"><i style={{ background: "var(--ink-muted)" }} /> Wand</span>
      <span className="sc-legend"><i style={{ background: "var(--limette)" }} /> Eingang</span>
      <span className="sc-legend"><i style={{ background: "var(--violett)" }} /> Kasse</span>
    </div>
  );
}
